<script setup lang="ts">
import { inject } from 'vue'
import FormFieldGroup from '@/components/FormSection/FormFieldGroup.vue'
import FormFieldError from '@/components/FormSection/FormFieldError.vue'
import DocModal from '@/components/DocModal.vue'

const termsUrl = import.meta.env.VITE_SA_TERMS_URL || ''
const termsBackupUrl = import.meta.env.VITE_SA_TERMS_BACKUP_URL || ''
const privacyUrl = import.meta.env.VITE_SA_PRIVACY_URL || ''
const privacyBackupUrl = import.meta.env.VITE_SA_PRIVACY_BACKUP_URL || ''

const { v$ }: any = inject('formState')
</script>

<template>
  <form-field-group
    class="authorisation-section border p-4 rounded text-start"
    :class="{
      'is-valid': !v$.agree.$error && v$.agree.$dirty,
      'is-invalid': v$.agree.$error && v$.agree.$dirty,
    }"
    data-testid="authorisation-section"
  >
    <h2 class="h4">{{ $t('form.authorisationStatement.title') }}</h2>
    <p>
      {{ $t('form.authorisationStatement.paragraph1') }}
    </p>
    <p data-testid="auth-text">
      {{ $t('form.authorisationStatement.authText') }}
    </p>

    <div class="row">
      <div class="col">
        <div class="checkbox-group">
          <input
            v-model="v$.agree.$model"
            class="form-check-input border-2 border-dark"
            type="checkbox"
            value=""
            id="agree"
            name="agree"
            :class="{
              'is-valid': !v$.agree.$error && v$.agree.$dirty,
              'is-invalid': v$.agree.$error && v$.agree.$dirty,
            }"
            data-testid="authorisation-checkbox"
          />
          <i18n-t
            keypath="form.authorisationStatement.checkboxLabel"
            scope="global"
            tag="label"
            for="agree"
            class="form-check-label fw-semibold"
          >
            <a
              :href="$t('form.authorisationStatement.termsLinkHref')"
              target="_blank"
              class="fw-semibold"
              data-bs-toggle="modal"
              data-bs-target="#terms-modal"
              @click.prevent=""
            >
              {{ $t('form.authorisationStatement.termsLinkText') }}
            </a>
            <a
              :href="$t('form.authorisationStatement.privacyLinkHref')"
              target="_blank"
              class="fw-semibold"
              data-bs-toggle="modal"
              data-bs-target="#privacy-modal"
            >
              {{ $t('form.authorisationStatement.privacyLinkText') }}
            </a>
          </i18n-t>
        </div>
      </div>
    </div>
    <form-field-error
      v-if="v$.agree.$error"
      :errors="v$.agree.$errors"
      css-class="small text-warning pt-3"
    ></form-field-error>
  </form-field-group>

  <doc-modal
    :content-url="termsUrl"
    :backup-url="termsBackupUrl"
    modal-id="terms-modal"
    :header="$t('termsModal.header')"
  />

  <doc-modal
    :content-url="privacyUrl"
    :backup-url="privacyBackupUrl"
    modal-id="privacy-modal"
    :header="$t('privacyModal.header')"
  />
</template>

<style lang="scss" scoped>
.authorisation-section {
  background-color: #f1f1f1;

  &.is-invalid {
    $invalidColor: #e28101;
    background-color: hsl(from var(--bs-warning-bg-subtle) h s l / 0.2);
    border-color: hsl(from var(--bs-warning) h s l / 0.2) !important;
  }

  &.is-valid {
    $validColor: #198754;
    background-color: hsl(from var(--bs-success-bg-subtle) h s l / 0.2);
    border-color: hsl(from var(--bs-success) h s l / 0.2) !important;
  }
}

.checkbox-group {
  display: grid;
  grid-auto-columns: 1fr;
  grid-template-columns: 0.1fr 1.9fr;
  grid-template-rows: 1fr;
  gap: 10px;

  @media (min-width: 992px) {
    gap: 0;
  }
}
</style>
